import { useCallback, useState } from 'react';
import type { IPlanYourTripProps } from 'components/common/PlanYourTrip/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import PartnerBlock from 'components/resort/PartnerBlock/PartnerBlock';
import { useSanitizedHtmlProps } from 'util/sanitized';
import { BlockContent } from 'components/blocks/BlockContent';
import DynamicLodgingForm from 'components/common/PlanYourTrip/Lodging/DynamicLodgingForm';
import DynamicSkiRentalForm from 'components/common/PlanYourTrip/SkiRental/DynamicSkiRentalForm';
import { isSkiRentalsLocale } from 'util/i18nFlags';
import TripControlBlock from './TripControlBlock';
import { PlaningViewsEnum } from './constants';

import styles from './styles.module.scss';

export const PlanYourTrip = ({
  title,
  tabDescription,
  redirectLodgingPath,
  initialTab,
  redirectSkiRentalPath,
  redirectBookFlightsPath,
  redirectCarRentalPath,
  hasSkiRental,
  hasFlights,
  hasCarRental,
  liftsUrl,
  rentalUrl,
  lessonsUrl,
  onLodgingChange,
  onSkiRentalChange,
  isPartner,
  contextKey,
  resortCountry,
  children,
}: IPlanYourTripProps) => {
  const { t } = useOTSTranslation('common');
  const [activeView, setActiveView] = useState<PlaningViewsEnum>(initialTab || PlaningViewsEnum.lodging);

  const onButtonClick = useCallback(
    (value: PlaningViewsEnum) => {
      setActiveView(value);
    },
    [activeView],
  );
  const htmlProps = useSanitizedHtmlProps(tabDescription || '');

  const getPartnerLink = useCallback(() => {
    if (activeView === PlaningViewsEnum.rental && rentalUrl) {
      return rentalUrl;
    }
    if (activeView === PlaningViewsEnum.lessons && lessonsUrl) {
      return lessonsUrl;
    }

    return undefined;
  }, [activeView, rentalUrl, lessonsUrl]);

  const showRentalAsForm = isSkiRentalsLocale() && hasSkiRental && !!redirectSkiRentalPath;

  return (
    <BoxWithShadow className={styles.paddingBottom}>
      <BlockHeader useH3 title={t('tripWidget.planYourTrip')} />
      <TripControlBlock
        title={title}
        liftsUrl={liftsUrl}
        activeView={activeView}
        rentalUrl={rentalUrl}
        lessonsUrl={lessonsUrl}
        onButtonClick={onButtonClick}
        redirectSkiRentalPath={redirectSkiRentalPath}
        redirectBookFlightsPath={redirectBookFlightsPath}
        redirectCarRentalPath={redirectCarRentalPath}
        redirectLodgingPath={redirectLodgingPath}
        isPartner={isPartner}
        contextKey={contextKey}
        resortCountry={resortCountry}
        hasSkiRental={hasSkiRental}
        hasFlights={hasFlights}
        hasCarRental={hasCarRental}
        initialTab={initialTab || PlaningViewsEnum.lodging}
      />
      {activeView === PlaningViewsEnum.lodging && (
        <DynamicLodgingForm
          searchRedirectUrl={redirectLodgingPath}
          onChange={onLodgingChange}
        />
      )}
      {showRentalAsForm && activeView === PlaningViewsEnum.rental && (
        <DynamicSkiRentalForm
          searchRedirectUrl={redirectSkiRentalPath}
          onChange={onSkiRentalChange}
        />
      )}
      {!showRentalAsForm && activeView === PlaningViewsEnum.rental && (
        <PartnerBlock
          partnerLinkUrl={getPartnerLink()}
          partnerType={activeView}
          title={title}
          contextKey="resort"
        />
      )}
      {activeView === PlaningViewsEnum.lessons && (
        <PartnerBlock
          partnerLinkUrl={getPartnerLink()}
          partnerType={activeView}
          title={title}
          contextKey="resort"
        />
      )}
      {activeView === PlaningViewsEnum.flights && tabDescription && (
        <BlockContent>
          <div {...htmlProps} />
        </BlockContent>
      )}
      {children}
    </BoxWithShadow>
  );
};
